// packages/frontend/src/components/RenameModal.tsx
import { useState, useEffect, useRef } from 'react'
import { useFileStore } from '../stores/fileStore'
import { X, File, Folder, Edit3 } from 'lucide-react'
import type { FileSystemItem } from '@shared'

interface RenameModalProps {
  isOpen: boolean
  onClose: () => void
  item: FileSystemItem | null
}

export function RenameModal({ isOpen, onClose, item }: RenameModalProps) {
  const [newName, setNewName] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { renameItem } = useFileStore()

  useEffect(() => {
    if (isOpen && item) {
      const currentName = item.item.name
      setNewName(currentName)
      setError('')
      
      // Focus and select filename (without extension for files)
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          
          if (item.type === 'file') {
            // Select filename without extension
            const lastDotIndex = currentName.lastIndexOf('.')
            if (lastDotIndex > 0) {
              inputRef.current.setSelectionRange(0, lastDotIndex)
            } else {
              inputRef.current.select()
            }
          } else {
            // Select all for folders
            inputRef.current.select()
          }
        }
      }, 100)
    }
  }, [isOpen, item])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!item || !newName.trim()) return

    const trimmedName = newName.trim()
    
    // Validation
    if (trimmedName === item.item.name) {
      onClose()
      return
    }

    if (trimmedName.length === 0) {
      setError('Name cannot be empty')
      return
    }

    if (trimmedName.length > 100) {
      setError('Name is too long (max 100 characters)')
      return
    }

    if (/[<>:"/\\|?*]/.test(trimmedName)) {
      setError('Name contains invalid characters')
      return
    }

    if (item.type === 'folder' && trimmedName.includes('/')) {
      setError('Folder names cannot contain "/"')
      return
    }

    setIsRenaming(true)
    setError('')

    try {
      await renameItem(item.item.id, trimmedName, item.type)
      onClose()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Rename failed')
    } finally {
      setIsRenaming(false)
    }
  }

  const handleClose = () => {
    if (!isRenaming) {
      onClose()
      setNewName('')
      setError('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  if (!isOpen || !item) return null

  const isFile = item.type === 'file'
  const originalName = item.item.name

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl bg-dark-800 p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-blue-500/20 p-2">
              {isFile ? (
                <File className="h-6 w-6 text-blue-400" />
              ) : (
                <Folder className="h-6 w-6 text-blue-400" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Rename {isFile ? 'File' : 'Folder'}
              </h2>
              <p className="text-dark-400 truncate max-w-48" title={originalName}>
                {originalName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isRenaming}
            className="rounded-lg p-2 text-dark-400 hover:bg-dark-700 hover:text-white disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="newName" className="block text-sm font-medium text-dark-200">
              New Name
            </label>
            <div className="relative mt-1">
              <input
                ref={inputRef}
                id="newName"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={handleKeyDown}
                className="input pl-12"
                disabled={isRenaming}
                placeholder={`Enter ${isFile ? 'file' : 'folder'} name`}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <Edit3 className="h-5 w-5 text-dark-400" />
              </div>
            </div>
            {error && (
              <p className="mt-1 text-sm text-red-400">{error}</p>
            )}
          </div>

          {/* File Extension Warning */}
          {isFile && newName.includes('.') && (
            <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Edit3 className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-400">
                    Changing the file extension may affect how the file opens.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isRenaming}
              className="btn btn-ghost disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!newName.trim() || newName.trim() === originalName || isRenaming}
              className="btn btn-primary disabled:opacity-50"
            >
              {isRenaming ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Renaming...
                </>
              ) : (
                <>
                  <Edit3 className="mr-2 h-4 w-4" />
                  Rename
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}